<template>
    <section>
        <section v-if="wallet?.invalid">
            <div class="alert" v-text="$t('error.invalid_address')"/>
        </section>

        <section v-show="!wallet?.invalid">
            <t-breadcrumbs class="t-breadcrumbs--page" v-bind:items="breadcrumbs"/>

            <div class="card">
                <div class="card-row" v-if="contractType === 'dns_domain' && addressMetadata?.extra?.domain">
                    <div class="card-row__name" v-text="$t('address.contract_type.domain_name')"/>
                    <div class="card-row__value">
                        <span class="card-domain-container">
                            <ui-copy-button v-bind:copy="addressMetadata.extra.domain">
                                {{ addressMetadata.extra.domain }}
                            </ui-copy-button>
                            <a v-bind:href="`http://${addressMetadata.extra.domain}`" target="_blank">
                                <icon-domain class="card-domain-icon card-main-qr-button" />
                            </a>
                        </span>
                    </div>
                </div>

                <div class="card-row" v-if="contractType === 'telegram_number' && addressMetadata?.name">
                    <div class="card-row__name" v-text="$t('address.contract_type.number')"/>
                    <div class="card-row__value">
                        <ui-copy-button v-bind:copy="addressMetadata.name">
                            {{addressMetadata.name}}
                        </ui-copy-button>
                    </div>
                </div>

                <div class="card-row" v-if="contractType === 'telegram_username' && addressMetadata?.name">
                    <div class="card-row__name" v-text="$t('address.contract_type.username')"/>
                    <div class="card-row__value">
                        <span class="card-domain-container">
                            <ui-copy-button v-bind:copy="addressMetadata.name.replace('@','') + '.t.me'">
                                {{addressMetadata.name}}
                            </ui-copy-button>
                            <a v-bind:href="`https://t.me/${addressMetadata.name.replace('@','')}`" target="_blank">
                                <icon-telegram class="card-domain-icon card-domain-icon--telegram card-main-qr-button" />
                            </a>
                        </span>
                    </div>
                </div>

                <div class="card-row">
                    <div class="card-row__name" v-text="$t('address.info.address')"/>
                    <div class="card-row__value">
                        <ui-copy-button class="card-main-address"
                            v-bind:successMessage="$t('address.info.copy_success')"
                            v-bind:copy="address"
                        >
                            {{address}}
                        </ui-copy-button>

                        <span class="card-main-qr-button" v-on:click="qrModalVisible = true">
                            <icon-qr class="card-main-qr-button__icon"/>
                        </span>
                        <a class="card-main-bell" v-bind:href="`https://t.me/TONNotifyBot?start=${address}`" target="_blank">
                            <icon-bell class="card-main-bell__icon"/>
                        </a>
                    </div>
                </div>

                <div class="card-row">
                    <div class="card-row__name" v-text="$t('address.info.balance')"/>
                    <div class="card-row__value" v-if="wallet.balance == '0' || wallet.balance">
                        <ui-gram-icon/><ui-animated-decimal v-bind:value="Number(wallet.balance)" />

                        <template v-if="wallet.balance != '0'">
                            <span v-if="$store.state.exchangeRate" class="fiat-value">
                                ≈ <ui-fiat v-bind:tonValue="wallet.balance"/>
                            </span>
                        </template>
                    </div>
                    <div v-else class="card-row__value">
                        <span class="skeleton">00000 GRAM ≈ 00000 USD</span>
                    </div>
                </div>

                <div class="card-row" v-if="stakedRowVisible">
                    <div class="card-row__name" v-text="$t('address.info.staked')"/>
                    <div v-if="stakedLoading" class="card-row__value">
                        <span class="skeleton">00000 GRAM</span>
                    </div>
                    <div v-else class="card-row__value staked-row">
                        <i18n v-if="stakeYearly && $store.state.exchangeRate" path="address.info.stake_per_year_fiat" tag="span">
                            <ui-fiat place="amount" v-bind:tonValue="stakeYearlyNanotons" v-bind:wholeAbove="100"/>
                        </i18n>
                        <span v-else-if="stakeYearly" v-text="$t('address.info.stake_per_year', { amount: stakeYearly })"/>
                        <ui-animated-decimal v-else v-bind:value="stakedNanotons" suffix=" GRAM"/>
                        <span v-if="stakedNanotons > 0 && $store.state.exchangeRate" class="fiat-value">
                            ≈ <ui-fiat v-bind:tonValue="stakedNanotons"/>
                        </span>
                        <a v-if="stakedNanotons > 0" class="staked-row__add" target="_blank" rel="noopener noreferrer"
                            v-bind:href="stakeUrl"
                            v-bind:title="$t('address.info.stake_button')"
                            v-bind:aria-label="$t('address.info.stake_button')">
                            <icon-plus class="staked-row__add-icon"/>
                        </a>
                        <a v-else class="card-row__value-tag staked-row__button"
                            v-bind:class="{ 'staked-row__button--shimmer': stakeYearly }" target="_blank" rel="noopener noreferrer"
                            v-bind:href="stakeUrl">
                            {{ $t(stakeYearly ? 'address.info.stake_start' : 'address.info.stake_button') }}
                        </a>
                    </div>
                </div>

                <div class="card-row" v-if="assetsRowVisible">
                    <div class="card-row__name" v-text="$t('address.info.assets')"/>
                    <div v-if="assetsLoaded" class="card-row__value top-user-jettons">
                        <ui-round-image v-for="jetton in topJettons.jetton_wallets" class="jetton-list__icon top-user-jettons__img" size="tiny" v-bind:src="getImgProxy(jetton.jetton)" v-bind:key="jetton.jetton" />
                        <div class="top-user-jettons__text">
                            {{ topJettonBalance }} {{ topJettonSymbol }}
                        </div>
                        <ui-link v-bind:to="{ name: 'address', hash: '#tokens', params: { address: this.address, dontRefresh: true }}">
                            {{ $t('address.info.and_more') }}
                        </ui-link>
                    </div>
                    <div v-else class="card-row__value top-user-jettons">
                        <span class="top-user-jettons__img skeleton"/>
                        <span class="top-user-jettons__text skeleton">123.45 TOKEN</span>
                    </div>
                </div>

                <div class="card-row">
                    <div class="card-row__name" v-text="$t('address.info.details')"/>
                    <div class="card-row__value">
                        <div class="card-row__value-tags">
                            <div
                                class="card-row__value-tag-item"
                                v-on:mouseover="showTooltip.state = true"
                                v-on:mouseleave="showTooltip.state = false"
                            >
                                <div v-if="wallet?.status === undefined || isSuspended === undefined" class="card-row__value-tag card-row__value-skeleton skeleton">
                                    <span>NNNNN</span>
                                </div>
                                <div v-else-if="wallet?.status === 'active'" class="card-row__value-tag card-row__value-tag-green">
                                    <span>{{ $t('address.info.type_active') }}</span>
                                </div>
                                <div v-else-if="wallet?.status === 'frozen'" class="card-row__value-tag card-row__value-tag-blue">
                                    <span>{{ $t('address.info.type_frozen') }}</span>
                                </div>
                                <ui-link v-bind:to="{name: 'suspended'}" v-else-if="isSuspended" class="card-row__value-tag card-row__value-tag-orange router-link__no-style">
                                    <span>{{ $t('address.info.type_suspended') }}</span>
                                </ui-link>
                                <div v-else class="card-row__value-tag card-row__value-tag-gray">
                                    <span>{{ $t('address.info.type_inactive') }}</span>
                                </div>
                                <ui-tooltip
                                    v-bind:value="$t('address.info.state')"
                                    position="top"
                                    v-if="showTooltip.state"
                                />
                            </div>
                            <div
                                v-if="contractTypeVisible && !isUninit"
                                class="card-row__value-tag-item"
                                v-on:mouseover="showTooltip.contract = true"
                                v-on:mouseleave="showTooltip.contract = false"
                            >
                                <div v-if="!contractType" class="card-row__value-tag card-row__value-skeleton skeleton">
                                    <span>NNNNN</span>
                                </div>
                                <div v-else class="card-row__value-tag card-row__value-tag-gray">
                                    {{ contractTypeFormatted }}
                                </div>
                                <ui-tooltip
                                    v-bind:value="$t('address.info.contract_type')"
                                    position="top"
                                    v-if="showTooltip.contract"
                                />
                            </div>
                            <div v-if="isScam" class="card-row__value-tag-item">
                                <div class="card-row__value-tag card-row__value-tag-red">
                                    <span>SCAM</span>
                                </div>
                            </div>
                            <div v-if="addressTag" class="card-row__value-tag-item">
                                <div class="card-row__value-tag card-row__value-tag-gray">
                                    <span>{{ addressTag }}</span>
                                </div>
                            </div>
                            <div v-if="walletName !== null" class="card-row__value-tag-item">
                                <div v-if="!walletName" class="card-row__value-tag card-row__value-skeleton skeleton">
                                    <span>NNNNN</span>
                                </div>
                                <ui-copy-button v-else hide-icon
                                    class="card-row__value-tag card-row__value-tag-gray-line"
                                    v-bind:copy="walletNameFull"
                                    v-bind:successMessage="$t('address.info.domain_copy_success')"
                                    v-on:click.native="openWalletName = true"
                                >
                                    <span>{{ walletName }}</span>
                                </ui-copy-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <address-tabs
                v-bind:address="addressDefault"
                v-bind:isActive="isActive"
                v-bind:shouldLoadPendings="loadPendings"
            >
                <template v-slot:customCalendar>
                    <export-csv v-if="$store.state.filterShow && !isMobile" v-bind:address="addressDefault" />
                    <custom-calendar v-if="$store.state.filterShow" />
                </template>
                <template v-slot:customFilter>
                    <custom-filter v-if="$store.state.filterShow" />
                    <nft-search v-if="$store.state.nftFilterShow" />
                    <nft-spam-toggle v-if="$store.state.nftFilterShow" />
                </template>
            </address-tabs>
        </section>

        <ui-modal class="qr-modal" v-bind:isOpen.sync="qrModalVisible">
            <ui-qr show-logo v-bind:value="`ton://transfer/${addressCanonical}`" v-bind:size="300"/>
        </ui-modal>

        <div style="display: none">
            <a ref="devExplorerLink" target="_blank">View in TON Explorer</a>
        </div>
    </section>
</template>

<script>
import IconQr from '@img/icons/tonscan/qr-14.svg?inline';
import IconDomain from '@img/icons/lucide/globe.svg?vue';
import UiGramIcon from '~/components/UiGramIcon.vue';
import IconTelegram from '@img/icons/tonscan/telegram.svg?vue';
import IconBell from '@img/icons/tonscan/bell.svg?inline';
import IconPlus from '@img/icons/tonscan/plus.svg?vue';
import { goToDevExplorerMixin } from '~/mixins';
import UiQr from '~/components/UiQr.vue';
import { formatRoundNumber, formatTons, truncateTicker } from '~/helpers';
import { isSameAddress } from '~/tonweb';
import { hidesDomain } from '~/lpPools.js';
import { LIQUID_STAKING_JETTONS, getStakedBalance, getOtherStakedNanotons } from '~/liquidStaking.js';
import { getAccountStates, getJettonsByOwnerAddress } from '~/api/toncenterV3';
import { breadcrumbsJsonLd, shortenCrumb } from '~/breadcrumbs.js';
import UiAnimatedDecimal from '~/components/UiAnimatedDecimal.vue';
import { mapState } from 'vuex';
import AddressTabs from './AddressTabs.vue';
import CustomFilter from './Filter/Filter.vue';
import NftSpamToggle from './NftSpamToggle.vue';
import NftSearch from './NftSearch.vue';
import CustomCalendar from './Calendar/Calendar.vue';
import ExportCsv from './ExportCsv.vue';
import contractHashes from '../../../json/contractHashes.json';

const STAKE_URL = 'https://t.me/StakeeBot?start=tonscan';
// Same rate as in the stake button and the Stakee banner texts:
const STAKE_APY = 0.2;

export default {
    props: {
        address: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            addressCanonical: this.address,
            contractType: undefined,
            contractTypeVisible: true,
            wallet: {},
            qrModalVisible: false,
            isSuspended: undefined,
            walletNameFull: undefined,
            openWalletName: false,
            addressDefault: this.address,
            showTooltip: {
                state: false,
                contract: false,
            },
            isUninit: false,
            addressMetadata: undefined,
            loadPendings: false,
            isRedirecting: false,
            assetsLoaded: false,
            staked: undefined, // { nanotons }, see liquidStaking.js
            otherStakedNanotons: 0, // other protocols' liquid staking, in GRAM
            stakeUrl: STAKE_URL,
        };
    },

    computed: {
        addressMeta() {
            return this.$store.getters.getAddressMeta(this.address);
        },

        // Home > the account, by the name it goes under or the ends of its address:
        breadcrumbs() {
            const name = this.$store.getters.getLocalAddressbookName(this.address)
                || this.addressMeta?.name
                || this.walletNameFull
                || shortenCrumb(this.addressCanonical);

            return [
                { name: this.$t('breadcrumbs.home'), to: { name: 'index' } },
                { name },
            ];
        },

        isScam() {
            return this.addressMeta?.isScam;
        },
        addressTag() {
            return this.addressMeta?.tag;
        },
        isActive() {
            return this.wallet?.status && this.wallet.status === 'active';
        },
        walletName() {
            if (!this.openWalletName && this.walletNameFull && this.walletNameFull.length > 20) {
                return this.walletNameFull.slice(0, 8) + '...' + this.walletNameFull.slice(-8);
            }
            return this.walletNameFull;
        },
        contractTypeFormatted() {
            if (this.contractType.startsWith('wallet')) {
                return this.contractType.replaceAll('_', ' ');
            }

            return this.$t(`address.contract_type.${this.contractType}`);
        },
        topJettons() {
            const jettons = this.$store.state.websocketAddressAssets;
            if (!jettons?.jetton_wallets) return {};

            const displayedJettons = jettons.jetton_wallets.slice(0, 3);

            return {
                jetton_wallets: displayedJettons?.reverse(),
                metadata: jettons.metadata,
                address_book: jettons.address_book,
            };
        },
        // Most addresses do hold something, so the row is kept in place with a
        // skeleton while the assets are loading instead of popping in later and
        // pushing everything below it down:
        assetsRowVisible() {
            if (this.wallet?.invalid || this.isUninit) return false;

            return !this.assetsLoaded || !this.topJettonsEmpty;
        },
        topJettonsEmpty() {
            // Hide jettons if address is uninitialized
            if (this.isUninit) return true;
            const jettons = this.$store.state.websocketAddressAssets;

            return !jettons?.jetton_wallets || jettons?.jetton_wallets.length === 0;
        },
        topJettonBalance() {
            const websocketAssets = this.$store.state.websocketAddressAssets;
            const lastJetton = websocketAssets.jetton_wallets.at(0);

            const lastJettonMetadata = websocketAssets.metadata[lastJetton.jetton]?.token_info[0]?.extra;
            const balance = lastJetton.balance;
            const decimals = parseInt(lastJettonMetadata?.decimals, 10) || 9;

            const shouldRound = balance > (100000 * 10 ** decimals);
            return formatTons(balance, decimals, shouldRound);
        },
        topJettonSymbol() {
            const lastJetton = this.$store.state.websocketAddressAssets.jetton_wallets.at(0);

            return this.getTopJettonTicker(lastJetton);
        },
        // Plain and Telegram wallets get the row even with nothing staked, as a way in.
        // Until the contract type is known it holds its place with a skeleton:
        stakedRowVisible() {
            if (this.wallet?.invalid || this.isUninit) return false;

            return this.contractType === undefined
                || this.stakedNanotons > 0
                || Boolean(this.contractType?.startsWith('wallet'))
                || this.contractType === 'tg_wallet';
        },
        // Jettons are still being fetched, or a pool rate is on its way:
        stakedLoading() {
            return this.contractType === undefined
                || !this.assetsLoaded
                || (Boolean(this.stakedJettonsKey) && this.staked === undefined);
        },
        stakedNanotons() {
            return this.staked?.nanotons || 0;
        },
        stakeYearlyNanotons() {
            return (Number(this.wallet?.balance || 0) + this.otherStakedNanotons) * STAKE_APY;
        },
        // With nothing staked, what the balance (plus liquid staking of other
        // protocols) would earn in a year at STAKE_APY:
        stakeYearly() {
            const nanotons = this.stakeYearlyNanotons;

            if (this.stakedNanotons > 0 || nanotons <= 0) return null;

            // From 1000 GRAM up the fraction is just noise:
            return nanotons >= 1000e9 ? formatRoundNumber(nanotons / 1e9) : formatTons(nanotons, 9, true);
        },
        // Liquid staking jettons of the account with their balances, as a key
        // so that the watcher fires only when a balance actually changes:
        stakedJettonsKey() {
            const jettons = this.$store.state.websocketAddressAssets?.jetton_wallets || [];

            return jettons
                .filter(wallet => LIQUID_STAKING_JETTONS[wallet.jetton])
                .map(wallet => `${wallet.jetton}:${wallet.balance}`)
                .join(',');
        },
        ...mapState(['websocketAccountState']),
    },

    watch: {
        stakedJettonsKey: 'loadStakedBalance',

        $route(newRoute) {
            if (!newRoute.params.dontRefresh) {
                this.loadData();
            }
        },

        // The tabs below fetch their own data off this address, and they
        // restart whenever it changes. Redirecting to the canonical spelling
        // of the very same account must not count as a change — otherwise
        // every EQ… link reloads its whole history a second time.
        address(newAddress) {
            if (!isSameAddress(newAddress, this.addressDefault)) {
                this.addressDefault = newAddress;
            }
        },

        websocketAccountState: {
            handler(newVal) {
                if (newVal?.balance) {
                    this.wallet.balance = newVal.balance;
                    this.wallet.status = newVal.account_status;
                }
            },
            deep: true,
            immediate: false,
        },
    },

    created() {
        this.loadData();
    },

    methods: {
        reset() {
            this.wallet = {};
            this.qrModalVisible = false;
            this.contractType = undefined;
            this.isSuspended = undefined;
            this.walletNameFull = undefined;
            this.openWalletName = false;
            this.isUninit = false;
            this.loadPendings = false;
            this.assetsLoaded = false;
            this.$store.state.websocketAddressAssets = {};
            this.staked = undefined;
            this.otherStakedNanotons = 0;
        },

        getTopJettonTicker(lastJetton) {
            if (this.topJettons.metadata[lastJetton.jetton]?.token_info[0]?.symbol) {
                return truncateTicker(this.topJettons.metadata[lastJetton.jetton].token_info[0].symbol);
            }
            const jettonAddress = this.topJettons.address_book[lastJetton.jetton].user_friendly;
            return jettonAddress.slice(0, 4) + '...' + jettonAddress.slice(44);
        },

        getImgProxy(jetton) {
            const metadata = this.topJettons.metadata[jetton]?.token_info[0];
            /* eslint-disable no-underscore-dangle */
            return metadata?.extra?._image_small || metadata?.extra?._image_big;
            /* eslint-enable no-underscore-dangle */
        },

        async checkAndRedirect(contractHash, metadataType) {
            let route;
            const getTypeByHash = contractHashes[contractHash] || 'unknown';

            switch (metadataType) {
                case 'jetton_masters':
                    route = 'token';
                    break;
                case 'nft_items':
                    route = 'nft';
                    break;
                case 'nft_collections':
                    route = 'collection';
                    break;
                default:
                    break;
            }

            switch (getTypeByHash) {
                case 'vesting_wallet':
                    route = 'vesting';
                    break;
                // All three live on /pool/:address, see PagePool.vue:
                case 'nominator_pool':
                case 'liquid_staking_pool':
                case 'single_nominator_v1_r0':
                case 'single_nominator_v1_r1':
                    route = 'pool';
                    break;
                case 'locker':
                    route = 'locker';
                    break;
                case 'jetton_wallet':
                    route = 'token-wallet';
                    break;
                case 'dedust_pool':
                case 'stonfi_pool':
                case 'stonfi_pool_v2':
                    route = 'lp';
                    break;
                // Fix-price sales and auctions share /auction/:address, see
                // PageAuction.vue. That page hands back the contracts it
                // cannot read, and sets the flag so they stay here:
                case 'nft_sale':
                case 'nft_auction':
                    if (!this.$route.params.skipListingRedirect) {
                        route = 'auction';
                    }
                    break;
                case 'multisig_wallet_v2':
                    route = 'multisig';
                    break;
                case 'multisig_order':
                    route = 'multisig_order';
                    break;
                default:
                    // Redirect to correct address format
                    if (this.address !== this.addressCanonical) {
                        await this.$router.replace(this.$localizeRoute({
                            name: 'address',
                            params: { address: this.addressCanonical, dontRefresh: true },
                        }));
                    }
            }

            if (route) {
                this.isRedirecting = true;
                await this.$router.replace(this.$localizeRoute({
                    name: route,
                    params: { address: this.addressCanonical },
                }));
            }

            this.contractType = getTypeByHash;
        },

        async loadData() {
            this.reset();
            let response;

            try {
                response = await getAccountStates(this.address);
            } catch (error) {
                // Means that address is invalid and doesn't exist
                this.wallet = {
                    invalid: true,
                };
                return;
            }

            this.wallet = response?.accounts[0];
            this.addressMetadata = response?.metadata[this.wallet?.address]?.token_info[0];
            // STON.fi routers and DeDust vaults go without a domain, see lpPools.js:
            const addressBookEntry = response?.address_book?.[this.wallet?.address];
            this.walletNameFull = hidesDomain(addressBookEntry?.interfaces, this.wallet?.code_hash)
                ? null
                : (addressBookEntry?.domain || null);

            if (!response?.accounts[0]) {
                // Means that the address is uninitialized
                this.isUninit = true;
                this.wallet = {
                    status: 'uninit',
                    balance: 0,
                };
                this.isSuspended = false;
                this.assetsLoaded = true;
                this.presaveUserAddressbook();
                return;
            }

            this.addressCanonical = response?.address_book[this.wallet?.address].user_friendly;

            const metadataType = response?.metadata[this.wallet?.address]?.token_info[0]?.type;
            await this.checkAndRedirect(this.wallet.code_hash, metadataType);
            if (this.isRedirecting) return;
            this.loadPendings = true;

            this.contractTypeVisible = this.wallet.status === 'active';
            this.isSuspended = this.wallet.status === 'suspended';

            this.presaveUserAddressbook();

            // Not awaited: the estimate shows from the balance first.
            this.loadOtherStaked();

            try {
                this.$store.state.websocketAddressAssets = await getJettonsByOwnerAddress(this.address, 3, 0, true);
            } catch (e) {
                console.error(e);
            } finally {
                this.assetsLoaded = true;
            }
        },

        // The staked row is derived from the top jettons already loaded for the
        // assets row: the only extra request is the pool rate, cached per pool.
        async loadStakedBalance() {
            const jettons = this.$store.state.websocketAddressAssets?.jetton_wallets;
            const key = this.stakedJettonsKey;

            if (!key) {
                this.staked = undefined;
                return;
            }

            const staked = await getStakedBalance(jettons);

            // The account may have changed while the rate was loading:
            if (key !== this.stakedJettonsKey) return;

            // An estimate, rounded to two decimals:
            staked.nanotons = Math.round(staked.nanotons / 1e7) * 1e7;
            this.staked = staked;
        },

        // Only wallets show the "per year" estimate this adds to.
        async loadOtherStaked() {
            const { address } = this;

            if (!this.contractType?.startsWith('wallet') && this.contractType !== 'tg_wallet') return;

            try {
                const nanotons = await getOtherStakedNanotons(address);

                if (address === this.address) this.otherStakedNanotons = nanotons;
            } catch (error) {
                console.error(error);
            }
        },

        presaveUserAddressbook() {
            const payload = {
                address: this.addressCanonical,
                name: this.addressMeta?.name || this.walletName,
                type: 'address',
            };
            this.$store.dispatch('presaveAddressbookItem', payload);
        },
    },

    metaInfo() {
        return {
            title: this.$t('address.meta.title', { address: this.addressCanonical }),
            meta: [{
                vmid: 'description', name: 'description',
                content: this.$t('address.meta.description', { address: this.addressCanonical }),
            }],
            script: [breadcrumbsJsonLd(this, this.breadcrumbs)].filter(Boolean),
        };
    },

    components: {
        UiGramIcon,
        UiAnimatedDecimal,
        IconDomain,
        IconTelegram,
        IconBell,
        IconPlus,
        IconQr,
        UiQr,
        AddressTabs,
        CustomFilter,
        NftSpamToggle,
        NftSearch,
        CustomCalendar,
        ExportCsv,
    },

    mixins: [goToDevExplorerMixin],
};
</script>

<style lang="scss">
.card-row {
    &__name-tags {
        flex-shrink: 0;
        padding-right: 2ex;
        white-space: nowrap;
        width: 100px;
        color: var(--body-muted-text-color);
        margin-top: 1px;
    }

    &__value-tags {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 5px;
    }

    &__value-tag-item {
        position: relative;
    }

    &__value-tag {
        font-weight: 600;
        user-select: none;
        padding: 2px 9px;
        border-radius: 6px;
        border: 1px solid;
        position: relative;
        font-size: 12px;
    }

    &__value-skeleton {
        border: 0;
        padding: 3px 9px;
    }

    &__value-tag-green {
        border-color: transparent;
        background: var(--address-tag-green-background);
        color: var(--address-tag-green-color);
    }

    &__value-tag-blue {
        border-color: var(--address-tag-blue-background);
        background: var(--address-tag-blue-background);
        color: var(--address-tag-blue-color);
    }

    &__value-tag-orange {
        border-color: var(--address-tag-orange-background);
        background: var(--address-tag-orange-background);
        color: var(--address-tag-orange-color);
    }

    &__value-tag-red {
        border-color: var(--address-tag-red-border);
        background: var(--address-tag-red-background);
        color: var(--address-tag-red-color);
    }

    &__value-tag-gray {
        border-color: transparent;
        background: var(--address-tag-gray-background);
        color: var(--address-tag-gray-color);
    }

    &__value-tag-gray-line {
        display: inline-block;
        background: transparent;
        border-color: var(--address-tag-gray-border);
        color: var(--body-text-color);
        opacity: .5;
        transition: opacity .15s;

        // Beats the text cursor the copy button sets on itself:
        &.ui-copy-button {
            cursor: pointer;
        }

        &:hover {
            opacity: 1;
        }
    }
}

.staked-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 10px;

    // A tag like the ones in the details row, so it is exactly their height;
    // the negative margin keeps it from making the row taller than its text:
    &__button {
        margin: -2px 0;
        border-color: transparent;
        color: var(--blue-bright);
        background: var(--blue-bright-background);
        white-space: nowrap;
        transition: background .12s;

        &:hover {
            text-decoration: none;
            background: var(--blue-bright-opacity);
        }

        // A light band that sweeps across the button now and then:
        &--shimmer {
            position: relative;
            overflow: hidden;

            &::after {
                content: '';
                position: absolute;
                inset: 0;
                background: linear-gradient(100deg, transparent 30%, rgba(255, 255, 255, .35) 50%, transparent 70%);
                transform: translateX(-100%);
                animation: staked-shimmer 5s ease-in-out infinite;
                pointer-events: none;
            }

            @media (prefers-reduced-motion: reduce) {
                &::after {
                    display: none;
                }
            }
        }
    }

    // Same colours as the stake button above, in a round pill sized to
    // the 16px coin icon of the balance row:
    &__add {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        margin: -1px 0 -1px -4px;
        border-radius: 50%;
        color: var(--blue-bright);
        background: var(--blue-bright-background);
        transition: background .12s;

        &:hover {
            background: var(--blue-bright-opacity);
        }
    }

    &__add-icon {
        width: 9px;
        height: 9px;
    }
}

@keyframes staked-shimmer {
    0% { transform: translateX(-100%); }
    30%, 100% { transform: translateX(100%); }
}

.top-user-jettons {
    display: flex;
    flex-direction: row;
    gap: 0;
    padding-left: 10px;
    align-items: center;
    margin: -2px 0 -2px 0;

    &__img {
        width: 20px;
        height: 20px;
        margin-left: -10px;
        margin-right: 0;
        border-radius: 50%;
        border: 2px solid var(--card-background);
        box-sizing: content-box;
        background-color: var(--card-background);
    }

    &__text {
        margin-left: 4px;
    }

    a {
        margin-left: 4px;
    }

    &__img.skeleton {
        border-radius: 50%;
        background: var(--body-light-muted-color);
    }

    &__text.skeleton {
        display: inline-block;
    }
}

.card-domain-container {
    display: inline-flex;
}

.card-domain-icon {
    display: inline-block;
    width: 16px;
    height: 16px;
    padding-left: 4px;
    transform: translateY(1px);

    &--telegram.card-main-qr-button {
        width: 15px;
        height: 15px;
        padding: 6px 2px 6px 6px;
        transform: translateY(1px);
    }
}
</style>
